import { useEditorStore } from '@/stores/editor.ts'
import { storeToRefs } from 'pinia'
import { useDeleteTable } from '@/views/editor/composable/table/useDeleteTable.ts'
import { useTable } from '@/views/editor/composable/table/useTable.ts'

let cachedSelectorMouseDownEvent: ((event: any) => void) | null = null

function getSelectorMouseDownEvent() {
	if (!cachedSelectorMouseDownEvent) {
		const { selectorMouseDownEvent } = useTable()
		cachedSelectorMouseDownEvent = selectorMouseDownEvent
	}
	return cachedSelectorMouseDownEvent
}

export function useSelectorOverlay() {
	let top = 0
	let left = 0
	let rbTop = 0
	let rbLeft = 0
	let targetRect: any = {}

	function setPosition() {
		const selector: HTMLDivElement | null = document.querySelector('.table--selector')
		const selectorLeftTop: HTMLDivElement | any = document.querySelector('.table--selector__pointer-left-top')
		const selectorRightBottom: HTMLDivElement | any = document.querySelector('.table--selector__pointer-right-bottom')

		const selectorMouseDownEvent = getSelectorMouseDownEvent()

		if (selectorLeftTop) {
			selectorLeftTop.removeEventListener('mousedown', selectorMouseDownEvent)
			selectorLeftTop.addEventListener('mousedown', selectorMouseDownEvent)
		}

		if (selectorRightBottom) {
			selectorRightBottom.removeEventListener('mousedown', selectorMouseDownEvent)
			selectorRightBottom.addEventListener('mousedown', selectorMouseDownEvent)
		}

		if (selector) {
			selector.style.top = `${top}px`
			selector.style.left = `${left}px`
			selector.style.width = `${targetRect.width + 3}px`
			selector.style.height = `${targetRect.height + 1}px`

			selectorLeftTop.style.top = `${top}px`
			selectorLeftTop.style.left = `${left}px`
			selectorRightBottom.style.top = `${rbTop}px`
			selectorRightBottom.style.left = `${rbLeft}px`

			const overlay = selector.closest('.table--selector__overlay') as HTMLElement
			if (overlay) overlay.style.visibility = 'visible'
		}
	}

	function positionSelector(target: HTMLDivElement) {
		if (!target) return

		const parentBlock: any = target.closest('.editor--block')
		const parentRect: any = parentBlock?.getBoundingClientRect()
		const parentCell: any = target.closest('td')

		if (!parentCell) {
			useDeleteTable().selector()
			return
		}
		targetRect = parentCell.getBoundingClientRect()
		top = targetRect.top - parentRect?.top
		left = targetRect.left - parentRect?.left
		rbTop = top + targetRect.height
		rbLeft = left + targetRect.width

		setPosition()
	}

	function positionMultiSelector(table: HTMLTableElement, dragList: any[]) {
		if (dragList.length < 1) return
		if (dragList.length < 2) {
			const target: any = dragList[dragList.length - 1]
			if (target) {
				positionSelector(target)
			}
			return
		}
		const { tableSelector } = storeToRefs(useEditorStore())
		const positions: any = []
		const processedCells = new Set()
		const processedPositions = new Set()

		dragList.forEach((item: any) => {
			if (processedCells.has(item)) return
			processedCells.add(item)

			tableSelector.value.forEach((row: any, index: number) => {
				row.forEach((cell: any, cellIndex: number) => {
					if (cell.html === item) {
						const posKey = `${index},${cellIndex}`
						if (!processedPositions.has(posKey)) {
							processedPositions.add(posKey)
							positions.push({
								row: index,
								col: cellIndex,
							})
						}
					}
				})
			})
		})

		let minRowIndex = Math.min(...positions.map((pos: any) => pos.row))
		let maxRowIndex = Math.max(...positions.map((pos: any) => pos.row))
		let minColIndex = Math.min(...positions.map((pos: any) => pos.col))
		let maxColIndex = Math.max(...positions.map((pos: any) => pos.col))

		// 병합 셀의 실제 시작 위치를 찾는 함수
		function findMergedCellOrigin(cellDom: any): { row: number, col: number } | null {
			for (let r = 0; r < tableSelector.value.length; r++) {
				for (let c = 0; c < tableSelector.value[r].length; c++) {
					if (tableSelector.value[r][c]?.html === cellDom) {
						return { row: r, col: c }
					}
				}
			}
			return null
		}

		const rePositions: any = []
		const processedMergedCells = new Set()
		
		for (let i = minRowIndex; i <= maxRowIndex; i++) {
			for (let j = minColIndex; j <= maxColIndex; j++) {
				const cell = tableSelector.value[i]?.[j]
				if (!cell?.html) continue
				
				// 이미 처리한 병합 셀은 건너뛰기
				if (processedMergedCells.has(cell.html)) continue
				processedMergedCells.add(cell.html)
				
				const rowSpan = cell.html.rowSpan || 1
				const colSpan = cell.html.colSpan || 1
				
				// 병합 셀의 실제 시작 위치 찾기
				const origin = findMergedCellOrigin(cell.html)
				if (!origin) continue
				

				
				// 병합 셀의 모든 영역을 rePositions에 추가 (실제 시작 위치 기준)
				for (let r = 0; r < rowSpan; r++) {
					for (let c = 0; c < colSpan; c++) {
						rePositions.push({
							row: origin.row + r,
							col: origin.col + c,
						})
					}
				}
			}
		}

		// rePositions에 포함된 셀들만 rect 수집 (병합 셀 영역만 포함, 주변 셀 제외)
		let allRects: any[] = []
		const rectProcessedCells = new Set()

		rePositions.forEach((pos: any) => {
			const cell = tableSelector.value[pos.row]?.[pos.col]
			if (cell?.html && !rectProcessedCells.has(cell.html)) {
				rectProcessedCells.add(cell.html)
				const td = cell.html.closest ? cell.html.closest('td') : cell.html
				if (td) {
					const rect = td.getBoundingClientRect()
					allRects.push(rect)
				}
			}
		})


		if (allRects.length === 0 || dragList.length === 0) return

		const minLeft = Math.min(...allRects.map(r => r.left))
		const minTop = Math.min(...allRects.map(r => r.top))
		const maxRight = Math.max(...allRects.map(r => r.right))
		const maxBottom = Math.max(...allRects.map(r => r.bottom))

		const firstCell = dragList[0]?.closest ? dragList[0].closest('td') : dragList[0]
		if (!firstCell) return
		const parentBlock = firstCell.closest('.editor--block')
		const parentRect: any = parentBlock?.getBoundingClientRect()

		const width = maxRight - minLeft
		const height = maxBottom - minTop


		rbLeft = maxRight - parentRect?.left
		rbTop = maxBottom - parentRect?.top

		top = minTop - parentRect?.top
		left = minLeft - parentRect?.left

		targetRect.width = width
		targetRect.height = height

		setPosition()
	}

	function handlerPosition(event: any) {
		const { enterSelector, isTemplateOpen, isHandlerTime } = storeToRefs(useEditorStore())
		const { target } = event

		const handlerElement: any = document.querySelector('.editor--handler')
		const handlerMenu = document.querySelector('.editor--handler__menu') as HTMLDivElement

		// container에서 이벤트가 발생한 경우 block 찾기
		const selectorElement: any = target.closest('.editor--block') || target.querySelector('.editor--block')

		if (!selectorElement) return

		const blockContainer = selectorElement.closest('.editor--block__container')
		if (blockContainer?.querySelector('.avatar--container')) {
			handlerElement.style.display = 'none'
			return
		}

		if (enterSelector.value) {
			if (enterSelector.value.id !== selectorElement.id) {
				handlerMenu.style.display = 'none'
			}
		}

		const wrap: any = document.querySelector('.editor--container__content')
		if (!isTemplateOpen.value) {
			enterSelector.value = selectorElement
		}

		const wrapRect: any = wrap.getBoundingClientRect()
		const targetRect: any = selectorElement.getBoundingClientRect()

		handlerElement.style.top = `${targetRect.top - wrapRect.top + 6}px`
		handlerElement.style.left = `${targetRect.left - wrapRect.left - 30}px`

		handlerElement.style.display = 'block'
		clearTimeout(isHandlerTime.value)
	}

	function disabledHandlerPosition() {
		const { isHandlerTime } = storeToRefs(useEditorStore())
		isHandlerTime.value = setTimeout(() => {
			const handlerElement: any = document.querySelector('.editor--handler')

			handlerElement.style.display = 'none'
		}, 500)
	}

	function useHandler() {
		const { isHandlerTime } = storeToRefs(useEditorStore())
		clearTimeout(isHandlerTime.value)
	}

	return {
		positionSelector,
		positionMultiSelector,
		handlerPosition,
		disabledHandlerPosition,
		useHandler,
	}
}
